import {Injectable} from '@angular/core';
import {ApgPortfolio} from 'projects/shared-components/adjustment-pricing-grid/model/ApgPortfolio';
import {SettingsStorageService} from 'projects/shared-components/settings-storage-service.service';
import {getShortUUID, isVoid} from 'projects/shared-components/utils';

/**
 * Store for INDEPENDENT Hedging Grid portfolios (CR-20260606-001).
 *
 * Independent portfolios are HG-only books with no APG parent. They are kept in a
 * SEPARATE user-settings key from APG's `apg.portfolios` so they never pollute the
 * APG / Comparison panels. Each carries its own immutable `asset` (underlying) and a
 * per-portfolio `defaultQty` (APG-bound portfolios derive that from their template,
 * which an independent portfolio does not have).
 *
 * Mirrors CashFlowPortfoliosService, minus the APG default-portfolio injection and the
 * comparison-grid (left/right) orientation — neither applies to independent books.
 */
const StorageKey = 'hg.independent-portfolios';

export interface HgIndependentPortfolioConfig {
    userId: string;
    userName: string;
}

@Injectable()
export class HgIndependentPortfoliosService {

    constructor(
        private readonly _settingsStorage: SettingsStorageService
    ) {
    }

    private _config: HgIndependentPortfolioConfig;

    configure(cfg: HgIndependentPortfolioConfig) {
        this._config = cfg;
    }

    getPortfolios(): ApgPortfolio[] {
        const portfolios = this._settingsStorage
            .getItem<ApgPortfolio[]>(StorageKey, this._config?.userId) || [];

        // Stamp the marker defensively so downstream guards always see it,
        // even for records persisted before the field existed.
        portfolios.forEach(p => p.isIndependent = true);

        portfolios.sort((a, b) => a.name.localeCompare(b.name));

        return portfolios;
    }

    savePortfolios(portfolios: ApgPortfolio[]) {
        this._settingsStorage.setItem(StorageKey, portfolios, this._config?.userId);
    }

    /**
     * Creates an independent portfolio with a fixed underlying (set once) and a base
     * order qty. Returns the created portfolio.
     */
    addPortfolio(args: { name: string, underlying: string, defaultQty?: number }): ApgPortfolio {

        const portfolio: ApgPortfolio = {
            id: getShortUUID(),
            name: args.name,
            asset: args.underlying,
            userId: this._config?.userId,
            userName: this._config?.userName,
            isIndependent: true,
            defaultQty: isVoid(args.defaultQty) ? 1 : args.defaultQty
        };

        const all = this.getPortfolios();
        all.push(portfolio);
        this.savePortfolios(all);

        return portfolio;
    }

    deletePortfolio(id: string) {
        const all = this.getPortfolios().filter(p => p.id !== id);
        this.savePortfolios(all);
    }
}
